import { isNil } from 'lodash'

import Field from 'stores/field/Field'

/**
 * Returns a string explaining why this field is invalid, or returns `undefined` to signify validity
 */
const isFieldValueInvalid = (field: Field, value: any): string | undefined => {
  if (field.isList) {
    if (!Array.isArray(value)) {
      return `Value must be a list`
    }

    // For lists, every value in the list must be valid as well
    return value.every(v => {
      if (field.isEnum) {
        return !_isEnumInvalid(field, v)
      }

      if (field.isRelation) {
        return !_isRelationInvalid(field, v)
      }

      return !_isScalarInvalid(field, v)
    })
      ? undefined
      : `Every value in this list must be valid`
  }

  if (field.isEnum) {
    return _isEnumInvalid(field, value)
  }

  if (field.isRelation) {
    return _isRelationInvalid(field, value)
  }

  return _isScalarInvalid(field, value)
}

const _isEnumInvalid = (field: Field, value: any): string | undefined => {
  if (!field.isRequired && isNil(value)) {
    // `null` values are valid if field is optional
    return undefined
  }

  if (value === undefined && field.default !== undefined) {
    // If value is `undefined`, but a default is defined, then the field is valid
    return undefined
  }

  const typeAsEnum = field.typeAsEnum
  if (!typeAsEnum) {
    return `Value must be an Enum variant`
  }

  return typeAsEnum.values.includes(value)
    ? undefined
    : `Value must be an Enum variant`
}

const _isRelationInvalid = (field: Field, value: any): string | undefined => {
  if (!field.isRequired && isNil(value)) {
    // `null` values are valid if field is optional
    return undefined
  }

  if (field.isRequired && !value) {
    // But not if the field is required
    return 'Required fields must not be `null`'
  }

  const typeAsModel = field.typeAsModel
  if (!typeAsModel) {
    // If the model being connected to does not exist, it is invalid. Should never happen
    return `Value must be a ${field.type} identifier`
  }

  // There's no real way for me to verify if the connection is valid, so assume it is
  return undefined
}

const _isScalarInvalid = (field: Field, value: any): string | undefined => {
  if (value === undefined && field.default !== undefined) {
    // If value is `undefined`, but a default is defined, then the field is valid
    return undefined
  }

  if (!field.isList && !field.isRequired && isNil(value)) {
    // `null` values are valid if field is optional
    return undefined
  }

  if (field.isRequired && isNil(value)) {
    // `null` values in a required field are never valid
    return 'Required fields must not be `null`'
  }

  if (field.isString && typeof value !== 'string') {
    return `Value must be a String`
  }

  if (
    field.isInt &&
    (typeof value !== 'number' || isNaN(value) || !Number.isInteger(value))
  ) {
    return `Value must be an Integer`
  }

  if (field.isFloat && (typeof value !== 'number' || isNaN(value))) {
    // We're explicitly not checking if the field is a Decimal
    // This is because Prisma Client returns Decimals as strings
    // FYI yes, this is a hack, sue me.
    return `Value must be a Float`
  }

  if (field.isBigInt && typeof value !== 'bigint') {
    return `Value must be a valid BigInt`
  }

  if (field.isBoolean && typeof value !== 'boolean') {
    return `Value must be a Boolean`
  }

  if (field.isDateTime && isNaN(new Date(String(value)) as any)) {
    return `Value must be a valid DateTime`
  }

  if (field.isJson && typeof value !== 'object') {
    // Checking typeof === 'object' is pretty safe here because of conditions above
    return `Value must be a valid Json`
  }

  return undefined
}

export default isFieldValueInvalid
